import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useChat } from '../contexts/ChatContext';
import { fetchOrders, isToday, isPastDue, daysBetween, getBinSize, fmtTime } from '../services/orderService';
import { fetchFlaggedTasks } from '../services/taskService';
import { queryKeys } from '../lib/queryClient';
import { formatCalendarDate } from '../lib/businessTime';
import CustomerDrawer from '../components/CustomerDrawer';
import {
  Text,
  Badge,
  Button,
  Avatar,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  Toaster,
  Link,
  useToastController,
  useId,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ShoppingBag24Regular,
  People24Regular,
  ArrowRight16Regular,
  Clock24Regular,
  Warning24Regular,
  VehicleTruck24Regular,
  ArrowDown24Regular,
  Flag24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflowX: 'hidden',
    marginLeft: '-1px',
  },

  // ── Hero ──────────────────────────────────────────────────────────
  hero: {
    background: 'linear-gradient(135deg, #4F8EF7 0%, #5BD2E6 60%, #C9F0F5 100%)',
    ...shorthands.padding('56px', '40px'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    position: 'relative',
    flexShrink: '0',
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('32px', '16px'),
      gap: '24px',
    },
  },
  heroGreeting: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    textShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  heroQuickRow: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gap: '12px',
    zIndex: '1',
  },
  heroQuickCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('14px', '20px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    cursor: 'pointer',
    ...shorthands.transition('all', '0.15s', 'ease'),
    '&:hover': {
      boxShadow: tokens.shadow16,
    },
    '&:hover > :first-child': {
      transform: 'scale(1.15)',
    },
  },
  heroQuickIconCircle: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('50%'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease',
  },
  heroQuickLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },

  // ── Dashboard body ─────────────────────────────────────────────────
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    ...shorthands.padding('24px', '28px'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('16px', '12px'),
    },
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    alignItems: 'start',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },

  // ── Panel ──────────────────────────────────────────────────────────
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px', '20px', '12px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  panelTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },

  // ── Message list ───────────────────────────────────────────────────
  messageList: {
    display: 'flex',
    flexDirection: 'column',
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  messageBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
  messageSender: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  messagePreview: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messageTime: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    flexShrink: '0',
  },
  messageMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    flexShrink: '0',
  },

  // ── Booking list ───────────────────────────────────────────────────
  bookingList: {
    display: 'flex',
    flexDirection: 'column',
  },
  bookingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  bookingTime: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    width: '64px',
    flexShrink: '0',
  },
  bookingBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
  bookingCustomer: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bookingMeta: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  bookingTypeIcon: {
    width: '28px',
    height: '28px',
    ...shorthands.borderRadius('6px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },

  // ── Doc / Invoice list ─────────────────────────────────────────────
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  listIconWrap: {
    width: '34px',
    height: '34px',
    ...shorthands.borderRadius('6px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  listBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
  listTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listSub: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  listRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    flexShrink: '0',
  },
});

function getGreeting(name: string): string {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return `${salutation}, ${name.split(' ')[0]}`;
}

function fmtDay(dateStr: string): string {
  // order.startDate is a rental day anchored at midnight UTC — render in UTC so
  // the day never drifts for viewers west of UTC.
  if (!dateStr) return '';
  return formatCalendarDate(dateStr);
}

// ── Sample fallbacks ──────────────────────────────────────────────────
// Shown only when the live query returns nothing, so every panel reads as
// "alive" during the demo. Each carries a "Sample" badge in its panel header
// so office users can tell preview data from their real data. Delete these
// (and the `*Sample` flags) once the panels are reliably backed by live data.
const SAMPLE_SCHEDULE = [
  { id: 's1', time: '8:30 AM',  customer: 'Heritage Homes',          type: 'Delivery' as const, bins: '20 yd', confirmed: true  },
  { id: 's2', time: '10:00 AM', customer: 'Blue Ridge Renovations',  type: 'Pickup'   as const, bins: '30 yd', confirmed: true  },
  { id: 's3', time: '1:15 PM',  customer: 'Thornbury Construction',  type: 'Delivery' as const, bins: '15 yd', confirmed: false },
  { id: 's4', time: '3:45 PM',  customer: 'Maple Leaf Landscaping',  type: 'Pickup'   as const, bins: '20 yd', confirmed: true  },
];

const SAMPLE_CONFIRMATIONS = [
  { id: 'c1', day: 'Tomorrow',     customer: 'Coastal Builders', bins: '30 yd' },
  { id: 'c2', day: 'Thu, Jun 12',  customer: 'Gareth Williams',  bins: '15 yd' },
  { id: 'c3', day: 'Fri, Jun 13',  customer: 'Summit Roofing',   bins: '20 yd' },
];

const SAMPLE_PAYMENTS = [
  { id: 'p1', customer: 'Thornbury Construction', amount: '$381.50', daysOverdue: 12 },
  { id: 'p2', customer: 'Gareth Williams',        amount: '$210.00', daysOverdue: 34 },
  { id: 'p3', customer: 'Coastal Builders',       amount: '$525.75', daysOverdue: 5  },
];

// Messages have no API yet — always sample for the demo (#TODO messaging API).
const SAMPLE_MESSAGES = [
  { id: '1', sender: 'Thornbury Construction', preview: 'Can we extend the rental by 3 days?',          time: '8m ago',    waitingOn: 'us'       as const },
  { id: '2', sender: 'Gareth Williams',        preview: 'I never received my invoice for last month',   time: '1h ago',    waitingOn: 'us'       as const },
  { id: '3', sender: 'Blue Ridge Renovations', preview: 'Sounds good, see you then',                    time: '3h ago',    waitingOn: 'customer' as const },
  { id: '4', sender: 'Maple Leaf Landscaping', preview: 'What sizes do you have available?',            time: 'Yesterday', waitingOn: 'us'       as const },
];

function SampleBadge() {
  return (
    <Badge appearance="outline" color="informative" size="small" style={{ marginLeft: '8px' }}>
      Sample
    </Badge>
  );
}

export default function OfficeDashboard() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const { openChat } = useChat();
  const queryClient = useQueryClient();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  const { data: orders = [] } = useQuery({
    queryKey: queryKeys.orders.all,
    queryFn: fetchOrders,
    staleTime: 5 * 60 * 1000,
  });

  const { data: flaggedTasks = [] } = useQuery({
    queryKey: ['tasks', 'flagged'],
    queryFn: fetchFlaggedTasks,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const isLive = (o: typeof orders[number]) =>
    o.booqableStatus !== 'canceled' && o.booqableStatus !== 'archived';

  // Today's deliveries (start = today) + pickups (end = today), time-ordered.
  const todaySchedule = useMemo(() => {
    const rows: { id: string; sort: number; time: string; customer: string; type: 'Delivery' | 'Pickup'; bins: string; confirmed: boolean }[] = [];
    for (const o of orders) {
      if (!isLive(o)) continue;
      const confirmed = o.booqableStatus === 'reserved' || o.booqableStatus === 'started';
      if (isToday(o.startDate)) {
        rows.push({ id: `${o.id}-d`, sort: new Date(o.startDate).getTime(), time: fmtTime(o.startDate), customer: o.customerName, type: 'Delivery', bins: getBinSize(o), confirmed });
      }
      if (isToday(o.endDate)) {
        rows.push({ id: `${o.id}-p`, sort: new Date(o.endDate).getTime(), time: fmtTime(o.endDate), customer: o.customerName, type: 'Pickup', bins: getBinSize(o), confirmed });
      }
    }
    return rows.sort((a, b) => a.sort - b.sort);
  }, [orders]);

  // Upcoming bookings not yet locked in (new/draft) — the office worklist.
  const needsConfirmation = useMemo(() =>
    orders
      .filter(o => (o.booqableStatus === 'new' || o.booqableStatus === 'draft') && !isPastDue(o.startDate))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .slice(0, 6)
      .map(o => ({ id: o.id, day: fmtDay(o.startDate), customer: o.customerName, bins: getBinSize(o) })),
    [orders]
  );

  const overdueInvoices = useMemo(() =>
    orders
      .filter(o => isPastDue(o.endDate) && o.price > o.paidAmount && isLive(o))
      .sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime())
      .map(o => ({
        id: o.id,
        customer: o.customerName,
        amount: `$${(o.price - o.paidAmount).toFixed(2)}`,
        daysOverdue: daysBetween(o.endDate),
      })),
    [orders]
  );

  // Real-where-it-exists, sample fallback so no panel is blank in the demo.
  const scheduleSample = todaySchedule.length === 0;
  const scheduleRows = scheduleSample ? SAMPLE_SCHEDULE : todaySchedule;

  const confirmSample = needsConfirmation.length === 0;
  const confirmRows = confirmSample ? SAMPLE_CONFIRMATIONS : needsConfirmation;

  const paymentsSample = overdueInvoices.length === 0;
  const paymentRows = paymentsSample ? SAMPLE_PAYMENTS : overdueInvoices;

  const quickActions = [
    { icon: <ShoppingBag24Regular style={{ color: '#fff' }} />, label: 'New Booking',  color: '#3DBE7A', onClick: () => navigate('/booking') },
    { icon: <People24Regular      style={{ color: '#fff' }} />, label: 'New Customer', color: '#4F8EF7', onClick: () => setAddCustomerOpen(true) },
  ];

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <Text className={styles.heroGreeting}>{user ? getGreeting(user.name) : 'Welcome'}</Text>
        <div className={styles.heroQuickRow}>
          {quickActions.map((item, i) => (
            <div key={i} className={styles.heroQuickCard} onClick={item.onClick}>
              <div className={styles.heroQuickIconCircle} style={{ backgroundColor: item.color }}>
                {item.icon}
              </div>
              <Text className={styles.heroQuickLabel}>{item.label}</Text>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.dashboard}>
        {/* Today's deliveries & pickups — the #1 office question, up top */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <Text className={styles.panelTitle}>
              Today's deliveries &amp; pickups
              {scheduleSample && <SampleBadge />}
            </Text>
            <Button appearance="transparent" size="small" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => navigate('/calendar')}>
              Calendar
            </Button>
          </div>
          <div className={styles.bookingList}>
            {scheduleRows.map(b => (
              <div key={b.id} className={styles.bookingItem} onClick={() => navigate('/orders')}>
                <Text className={styles.bookingTime}>{b.time}</Text>
                <div
                  className={styles.bookingTypeIcon}
                  style={{ backgroundColor: b.type === 'Delivery' ? '#EAF2FF' : '#E6F9F0' }}
                >
                  {b.type === 'Delivery'
                    ? <VehicleTruck24Regular style={{ color: '#4F8EF7', fontSize: '16px' }} />
                    : <ArrowDown24Regular style={{ color: '#3DBE7A', fontSize: '16px' }} />}
                </div>
                <div className={styles.bookingBody}>
                  <Text className={styles.bookingCustomer}>{b.customer}</Text>
                  <Text className={styles.bookingMeta}>{b.type} · {b.bins}</Text>
                </div>
                <Badge appearance="tint" color={b.confirmed ? 'success' : 'warning'}>
                  {b.confirmed ? 'Confirmed' : 'Unconfirmed'}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Row: Bookings needing confirmation + Payments to collect */}
        <div className={styles.twoCol}>
          {/* Bookings needing confirmation */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <Text className={styles.panelTitle}>
                Bookings needing confirmation
                {confirmSample && <SampleBadge />}
              </Text>
              <Button appearance="transparent" size="small" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => navigate('/orders')}>
                View all
              </Button>
            </div>
            <div>
              {confirmRows.map(c => (
                <div key={c.id} className={styles.listItem} onClick={() => navigate('/orders')}>
                  <div className={styles.listIconWrap} style={{ backgroundColor: '#FFF4DE' }}>
                    <Clock24Regular style={{ color: '#C27820', fontSize: '18px' }} />
                  </div>
                  <div className={styles.listBody}>
                    <Text className={styles.listTitle}>{c.customer}</Text>
                    <Text className={styles.listSub}>{c.day} · {c.bins}</Text>
                  </div>
                  <div className={styles.listRight}>
                    <Badge appearance="tint" color="warning">Confirm</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payments to collect */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <Text className={styles.panelTitle}>
                Payments to collect
                {paymentsSample && <SampleBadge />}
              </Text>
              <Warning24Regular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '18px' }} />
            </div>
            <div>
              {paymentRows.map(inv => (
                <div key={inv.id} className={styles.listItem} onClick={() => navigate('/financial')}>
                  <div className={styles.listIconWrap} style={{ backgroundColor: inv.daysOverdue > 30 ? '#FDEAEA' : '#FFF4DE' }}>
                    <Warning24Regular style={{ color: inv.daysOverdue > 30 ? '#E05252' : '#C27820', fontSize: '18px' }} />
                  </div>
                  <div className={styles.listBody}>
                    <Text className={styles.listTitle}>{inv.customer}</Text>
                    <Text className={styles.listSub}>{inv.daysOverdue} days overdue</Text>
                  </div>
                  <div className={styles.listRight}>
                    <Text style={{ fontSize: '14px', fontWeight: '700', color: tokens.colorNeutralForeground1 }}>{inv.amount}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flagged jobs — full width, only when there are problems (live data) */}
        {flaggedTasks.length > 0 && (
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <Text className={styles.panelTitle}>
                Flagged jobs
                <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: '8px' }}>
                  {flaggedTasks.length}
                </Badge>
              </Text>
              <Button appearance="transparent" size="small" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => navigate('/dispatch')}>
                View in dispatch
              </Button>
            </div>
            <div>
              {flaggedTasks.map(task => (
                <div key={task.id} className={styles.listItem} onClick={() => navigate('/dispatch')}>
                  <div className={styles.listIconWrap} style={{ backgroundColor: '#FDEAEA' }}>
                    <Flag24Regular style={{ color: '#C4314B', fontSize: '18px' }} />
                  </div>
                  <div className={styles.listBody}>
                    <Text className={styles.listTitle}>{task.customer_name}</Text>
                    <Text className={styles.listSub}>
                      {task.task_type === 'return' ? 'Pickup' : 'Delivery'} · {task.location || 'N/A'}
                      {task.notes ? ` — "${task.notes}"` : ''}
                    </Text>
                  </div>
                  <div className={styles.listRight}>
                    <Badge appearance="tint" color="danger">Problem</Badge>
                    {task.driver && (
                      <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{task.driver}</Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages & callbacks — sample until the messaging API lands */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <Text className={styles.panelTitle}>
              Messages &amp; callbacks
              {SAMPLE_MESSAGES.filter(m => m.waitingOn === 'us').length > 0 && (
                <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: '8px' }}>
                  {SAMPLE_MESSAGES.filter(m => m.waitingOn === 'us').length}
                </Badge>
              )}
              <SampleBadge />
            </Text>
            <Button appearance="transparent" size="small" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => navigate('/messaging')}>
              View all
            </Button>
          </div>
          <div className={styles.messageList}>
            {SAMPLE_MESSAGES.map(m => (
              <div
                key={m.id}
                className={styles.messageItem}
                onClick={() => openChat({ id: 100 + parseInt(m.id), name: m.sender, avatar: m.sender, preview: m.preview })}
              >
                <Avatar name={m.sender} size={32} color="colorful" />
                <div className={styles.messageBody}>
                  <Text className={styles.messageSender}>{m.sender}</Text>
                  <Text className={styles.messagePreview}>{m.preview}</Text>
                </div>
                <div className={styles.messageMeta}>
                  <Text className={styles.messageTime}>{m.time}</Text>
                  <Badge
                    appearance="tint"
                    color={m.waitingOn === 'us' ? 'warning' : 'informative'}
                    size="small"
                  >
                    {m.waitingOn === 'us' ? 'Reply needed' : 'Awaiting reply'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add-customer drawer, opened in-place by the New Customer quick action
          so office staff stay on the dashboard. The kind selector replaces the
          Customers-page Add SplitButton, which isn't present on this surface. */}
      <CustomerDrawer
        isOpen={addCustomerOpen}
        showKindSelector
        onClose={() => setAddCustomerOpen(false)}
        onSaved={(customerData) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
          const kind = (customerData as any)?.customerKind;
          const entityLabel =
            kind === 'company' ? 'Company' : kind === 'contact' ? 'Contact' : 'Customer';
          // Link the toast to the new record so staff can either carry on with
          // the dashboard or jump straight to the profile they just created.
          const newId = (customerData as any)?.id;
          dispatchToast(
            <Toast>
              <ToastTitle
                action={
                  newId ? (
                    <ToastTrigger>
                      <Link onClick={() => navigate(`/customers/${newId}`)}>View profile</Link>
                    </ToastTrigger>
                  ) : undefined
                }
              >
                {`${entityLabel} created`}
              </ToastTitle>
              <ToastBody>{`${entityLabel} has been successfully added.`}</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />
      <Toaster toasterId={toasterId} />
    </div>
  );
}
